/** Presentation only. Coupon eligibility and totals remain server-owned. */
export const RETURNING_OFFER = {
  code: "WELCOME10",
  percent: 10,
  minSeenCount: 3,
  delayMs: 3500,
  visitorKey: "tw_returning_visitor",
  dismissKey: "tw_returning_reminder_dismissed",
  shownKey: "tw_returning_reminder_shown_v1",
} as const;

export type ReturningVisitor = { seenCount: number; primaryInterest: string | null };
export type OfferProduct = {
  handle: string;
  title: string;
  images: { edges: Array<{ node: { url: string; altText: string | null } }> };
};

/** Optional art direction. New public catalogue products do NOT need an entry. */
export const RETURNING_PRODUCT_ART: Record<string, {
  src: string;
  tone: "mag" | "ova" | "liver";
  caption: string;
}> = {
  "mag-absorb-pro": { src: "/images/truwe-products/mag-absorb-pro.webp", tone: "mag", caption: "60 tablets" },
  "ova-balance": { src: "/images/truwe-products/ova-balance.webp", tone: "ova", caption: "30 sachets" },
  "liver-biome-plus": { src: "/images/truwe-products/liver-biome-plus.webp", tone: "liver", caption: "60 tablets" },
};

export function normaliseProductHandle(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const handle = value.trim();
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(handle) && handle.length <= 80 ? handle : null;
}

export function readReturningVisitor(value: unknown): ReturningVisitor | null {
  if (!value || typeof value !== "object") return null;
  const input = value as Record<string, unknown>;
  if (typeof input.seenCount !== "number" || !Number.isSafeInteger(input.seenCount)
    || input.seenCount < RETURNING_OFFER.minSeenCount) return null;
  return { seenCount: input.seenCount, primaryInterest: normaliseProductHandle(input.primaryInterest) };
}

export function productHandleFromPath(path: string): string | null {
  const value = /^\/products\/([^/]+)\/?$/.exec(path)?.[1];
  if (!value) return null;
  try { return normaliseProductHandle(decodeURIComponent(value)); } catch { return null; }
}

export function returningOfferCandidates(path: string, interest: string | null, cartHandles: string[]): string[] {
  return [...new Set([productHandleFromPath(path), interest, ...cartHandles]
    .map(normaliseProductHandle).filter((handle): handle is string => !!handle))];
}

export function hideReturningOffer(path: string, hostname: string): boolean {
  if (hostname.startsWith("admin.")) return true;
  return ["/admin", "/_authenticated", "/checkout", "/order-success", "/account", "/auth", "/policies", "/affiliate", "/api"]
    .some((prefix) => path === prefix || path.startsWith(prefix + "/"));
}

export function offerImageSources(product: OfferProduct): string[] {
  return [...new Set([RETURNING_PRODUCT_ART[product.handle]?.src,
    ...product.images.edges.map(({ node }) => node.url)]
    .filter((url): url is string => typeof url === "string" &&
      (/^\/(?!\/)/.test(url) || /^https?:\/\//i.test(url))))];
}
